import * as Tabs from '@radix-ui/react-tabs';
import React, {ComponentPropsWithoutRef, FC, ReactNode} from 'react';

import {OTP_TYPE_CODE, OtpTypeCode} from '@/consts/loginTypes';
import appContent from '@/services/content/appContent.typed';
import type {OtpContentModel} from '@/services/content/appContent.types';

interface TabTriggerProps extends ComponentPropsWithoutRef<'button'> {
    value: string;
}

const TabTrigger: FC<TabTriggerProps> = ({value, children, ...rest}) => {
    return (
        <Tabs.Trigger
            value={value}
            className='flex-1 cursor-pointer border-b border-b-transparent p-4 text-center text-16 font-medium text-primary-dark transition-colors data-[state=active]:border-primary data-[state=active]:font-bold'
            {...rest}
        >
            {children}
        </Tabs.Trigger>
    );
};

const getChannelLabel = (channel: OtpTypeCode, otpContent: OtpContentModel | undefined): string => {
    if (!otpContent) return channel;

    switch (channel) {
        case OTP_TYPE_CODE.EMAIL:
            return otpContent.emailTabName || channel;
        case OTP_TYPE_CODE.PHONE:
            return otpContent.phoneTabName || channel;
        default:
            return channel;
    }
};

interface OtpChannelTabsProps {
    activeChannel: OtpTypeCode;
    channels: OtpTypeCode[];
    onChannelChange?: (channel: OtpTypeCode) => void;
    className?: string;
    hasTabs?: boolean;
    children?: ReactNode;
}

const OtpChannelTabs: FC<OtpChannelTabsProps> = ({
    activeChannel,
    channels,
    onChannelChange,
    className,
    children,
    hasTabs,
}) => {
    const otpContent = appContent.getOtpContent();

    const handleValueChange = (nextValue: string): void => {
        if (!nextValue) return;
        onChannelChange?.(nextValue as OtpTypeCode);
    };

    return (
        <Tabs.Root className={className} value={activeChannel} onValueChange={handleValueChange}>
            {hasTabs && (
                <Tabs.List className='bg-secondary-light flex border-b-[1px] border-primary-dark-25'>
                    {channels.map((channel) => (
                        <TabTrigger key={channel} value={channel}>
                            {getChannelLabel(channel, otpContent)}
                        </TabTrigger>
                    ))}
                </Tabs.List>
            )}

            <Tabs.Content value={activeChannel}>{children}</Tabs.Content>
        </Tabs.Root>
    );
};

export default OtpChannelTabs;
